import gql from 'graphql-tag';
import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import InfoIcon from '@watershed/icons/components/Info';
import Callout from '@watershed/shared-frontend/components/Callout';
import useSnackbar from '@watershed/shared-frontend/hooks/useSnackbar';
import must from '@watershed/shared-universal/utils/must';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import Button from '@watershed/ui-core/components/Button';
import CopyButton from '@watershed/ui-core/components/CopyButton';
import { AutocompleteFieldNonFormik } from '@watershed/ui-core/components/Form/AutocompleteField';
import {
  TextFieldMultilineNonFormik,
  TextFieldNonFormik,
} from '@watershed/ui-core/components/Form/TextField';
import ProgressButton from '@watershed/ui-core/components/ProgressButton';
import StepDialog, {
  StepDialogStep,
} from '@watershed/ui-core/components/StepDialog';

import { GQCreateOauthClientInput } from '../../../generated/graphql-schema-types';
import {
  useAllRolesQuery,
  useCreateApiOAuthClientMutation,
} from '../../../generated/urql';
import ErrorPage from '../../ErrorPage';
import {
  getDefaultDurationMinutes,
  MAX_API_KEY_EXPIRY_MINUTES,
  MIN_API_KEY_EXPIRY_MINUTES,
  minutesToMs,
} from '../api/oauthClientDurationUtils';

gql`
  mutation CreateApiOAuthClient($input: CreateOauthClientInput!)
  @withOwner(owner: EnterpriseFoundations) {
    createOauthClient(input: $input) {
      client {
        id
        name
        description
      }
      secret
    }
  }
`;

type ClientIdAndSecret = {
  clientId: string;
  secret: string;
};

export default function CreateApiOauthClientDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useLingui();
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const onNext = () => setCurrentStep((curr) => curr + 1);
  const [createInput, setCreateInput] = useState<GQCreateOauthClientInput>({
    name: '',
    description: '',
    roleIds: [],
    apiKeyExpiryMs: minutesToMs(getDefaultDurationMinutes()),
  });
  const [durationMinutes, setDurationMinutes] = useState<string>(
    getDefaultDurationMinutes().toString()
  );
  const [, executeCreate] = useCreateApiOAuthClientMutation();
  const [clientIdAndSecret, setClientIdAndSecret] =
    useState<ClientIdAndSecret | null>(null);
  const resetCreateInput = () => {
    setCreateInput({
      name: '',
      roleIds: [],
      apiKeyExpiryMs: minutesToMs(getDefaultDurationMinutes()),
    });
    setDurationMinutes(getDefaultDurationMinutes().toString());
  };
  const resetClientIdAndSecret = () => {
    setClientIdAndSecret(null);
  };
  const isDurationValid = () => {
    const minutes = parseInt(durationMinutes, 10);
    return (
      !isNaN(minutes) &&
      minutes >= MIN_API_KEY_EXPIRY_MINUTES &&
      minutes <= MAX_API_KEY_EXPIRY_MINUTES
    );
  };

  const isSubmitDisabled =
    createInput.name === '' ||
    createInput.roleIds.length === 0 ||
    !isDurationValid();
  const { enqueueSnackbar } = useSnackbar();

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      resetCreateInput();
      resetClientIdAndSecret();
      setCurrentStep(1);
    }, 500);
  };

  const onSubmit = async () => {
    setSubmitting(true);
    const result = await executeCreate({
      input: createInput,
    });
    if (result.error) {
      enqueueSnackbar('Error creating OAuth credentials.', {
        variant: 'error',
      });
      return;
    }

    const client = result.data?.createOauthClient?.client;
    const secret = result.data?.createOauthClient?.secret;

    if (!client || !secret) {
      enqueueSnackbar('Error creating OAuth credentials.', {
        variant: 'error',
      });
      return;
    }

    setClientIdAndSecret({
      clientId: client.id,
      secret,
    });
    setSubmitting(false);
    onNext();
    resetCreateInput();
  };

  const [rolesResult] = useAllRolesQuery();

  if (rolesResult.error) {
    return <ErrorPage error={rolesResult.error} />;
  }
  const roles =
    rolesResult.data?.allRoles.filter((role) => role.workosGroupId === null) ??
    []; // don't allow api keys to be assigned to SCIM roles

  const steps: Array<StepDialogStep> = [
    {
      children: (
        <Stack gap={3}>
          <TextFieldNonFormik
            data-testid={TestIds.ApiSettingsDialogNameField}
            id="name"
            label={t({ message: 'Name', context: 'form field label' })}
            required
            onChange={(e) =>
              setCreateInput({ ...createInput, name: e.target.value })
            }
          />
          <TextFieldMultilineNonFormik
            data-testid={TestIds.ApiSettingsDialogDescriptionField}
            rows={2}
            id="description"
            label={t({ message: 'Description', context: 'form field label' })}
            placeholder={t({
              message: 'Add a note…',
              context: 'multiline form field placeholder',
            })}
            onChange={(e) =>
              setCreateInput({ ...createInput, description: e.target.value })
            }
            required={false}
          />
          <AutocompleteFieldNonFormik
            data-testid={TestIds.ApiSettingsDialogRolesField}
            label={t({ message: 'Roles', context: 'form field label' })}
            sublabel={t({
              message:
                'These credentials will inherit the permissions associated with the role(s) selected.',
              context: 'form field sublabel',
            })}
            id="roleIds"
            options={roles.map((r: { id: string }) => r.id)}
            getOptionLabel={(roleId) =>
              must(roles.find((r: { id: string }) => r.id === roleId)).name
            }
            multiple
            required
            onChange={(_, value) =>
              setCreateInput({
                ...createInput,
                roleIds: value.map((v) =>
                  typeof v === 'string' ? v : (v as { value: string }).value
                ) as Array<string>,
              })
            }
          />
          <TextFieldNonFormik
            label={t({
              message: 'Token expiration (minutes)',
              context: 'form field label',
            })}
            sublabel={t({
              message: `Tokens will expire after this duration. Min: ${MIN_API_KEY_EXPIRY_MINUTES}, Max: ${MAX_API_KEY_EXPIRY_MINUTES} (1 day)`,
              context:
                'form field sublabel, min/max refer to minimum and maximum',
            })}
            id="apiKeyExpiryMinutes"
            type="number"
            step={1}
            min={MIN_API_KEY_EXPIRY_MINUTES}
            max={MAX_API_KEY_EXPIRY_MINUTES}
            value={durationMinutes}
            validationState={isDurationValid() ? 'default' : 'error'}
            validationMessage={
              isDurationValid()
                ? undefined
                : t({
                    message: 'Please enter a valid duration',
                    context: 'form field validation message',
                  })
            }
            onChange={(e) => {
              const value = e.target.value;

              // Prevent decimal input - only allow integers
              if (
                value !== '' &&
                (value.includes('.') || value.includes(','))
              ) {
                return;
              }

              setDurationMinutes(value);

              const minutes = parseInt(value, 10);
              if (!isNaN(minutes) && minutes > 0) {
                setCreateInput({
                  ...createInput,
                  apiKeyExpiryMs: minutesToMs(minutes),
                });
              }
            }}
            required
          />
        </Stack>
      ),
      dialogProps: {
        header: {
          title: t({
            message: 'Create OAuth credentials',
            context: 'form title',
          }),
        },
        actions: (
          <ProgressButton
            data-testid={TestIds.ApiSettingsDialogCreateButton}
            onClick={onSubmit}
            isInProgress={submitting}
            disabled={isSubmitDisabled}
            tooltip={
              isSubmitDisabled
                ? t({
                    message: 'Please fill out all required fields',
                    context: 'form tooltip',
                  })
                : undefined
            }
          >
            <Trans context="Button copy">Create OAuth credentials</Trans>
          </ProgressButton>
        ),
      },
    },
    {
      children: (
        <Stack gap={2}>
          <Callout
            variant="warning"
            IconComponent={InfoIcon}
            description={
              <Trans context="Create OAuth credentials step dialog">
                Make sure you save your credentials, as you won't be able to
                view them again. Keep them secure; anyone with your credentials
                can make requests on your behalf. If you lose them, you'll need
                to generate a new one.
              </Trans>
            }
          />
          <Stack
            direction="row"
            sx={{
              gap: 1,
              alignItems: 'flex-end',
              width: '100%',
            }}
          >
            <TextFieldNonFormik
              id="clientId"
              sx={{
                flexGrow: 1,
              }}
              label={
                <Typography variant="h4">
                  <Trans context="form field label">Client ID</Trans>
                </Typography>
              }
              value={clientIdAndSecret?.clientId}
              // @ts-expect-error - InputProps is valid for TextFieldNonFormik
              InputProps={{
                readOnly: true,
              }}
            />
            <CopyButton
              text={clientIdAndSecret?.clientId}
              iconOnly
              color="primary"
              size="large"
            />
          </Stack>
          <Stack
            direction="row"
            sx={{
              gap: 1,
              alignItems: 'flex-end',
              width: '100%',
            }}
          >
            <TextFieldNonFormik
              id="secret"
              sx={{
                flexGrow: 1,
              }}
              label={
                <Typography variant="h4">
                  <Trans context="form field label">Client secret</Trans>
                </Typography>
              }
              value={clientIdAndSecret?.secret}
              // @ts-expect-error - InputProps is valid for TextFieldNonFormik
              InputProps={{
                readOnly: true,
              }}
            />
            <CopyButton
              text={clientIdAndSecret?.secret}
              iconOnly
              color="primary"
              size="large"
            />
          </Stack>
        </Stack>
      ),
      dialogProps: {
        header: {
          title: t({
            message: 'Create OAuth credentials',
            context: 'form title',
          }),
          hideClose: true,
        },
        actions: (
          <Button
            data-testid={TestIds.ApiSettingsDialogDoneButton}
            onClick={handleClose}
            color="primary"
          >
            <Trans context="Button copy for finishing the oauth flow">
              Finish
            </Trans>
          </Button>
        ),
      },
    },
  ];

  return (
    <StepDialog
      stepCount={steps.length}
      currentStep={currentStep}
      steps={steps}
      onStepChange={setCurrentStep}
      defaultDialogProps={{
        open,
        onClose: handleClose,
      }}
      // we disable step change because going backwards is not supported
      disableStepChange
    />
  );
}
